// Organisatie → Implementatiepartner (B5B-PARTNER-COCKPIT §7 / OWNER-issue 10; PATH-B W37). The tenant-side
// of the partner relationship: engage a consultancy firm from the directory and invite its consultants —
// which makes the partner cockpit reachable for those consultants. Talks to the auth-worker (owner registry),
// credentialed, like the Team surface. Gated via the auth-worker (tenant-admin codes).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Handshake, UserPlus, Trash2, Power } from "lucide-react";
import { AUTH_BASE } from "../../lib/env";
import { useI18n } from "../../lib/i18n";
import { Can } from "../../lib/rbac";
import { PageHeader } from "../../components/page-header";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

async function authFetch(path: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(`${AUTH_BASE}${path}`, { ...init, credentials: "include", headers: { "content-type": "application/json", ...(init?.headers ?? {}) } });
  const text = await res.text();
  const body = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) throw new Error(typeof body === "object" && body && "error" in body ? String((body as Record<string, unknown>).error) : `HTTP ${res.status}`);
  return body;
}

interface Firm { id: string; legalName: string; tradeName: string | null; certified: boolean }
interface Engagement { id: string; consultancyFirmId: string; firmName: string; status: string; contractReference: string | null; consultants: number }
interface Consultant { id: string; userId: string; email: string; fullName: string | null; role: string; status: "pending" | "active"; revoked: boolean }

export function ImplementatiepartnerRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [firmId, setFirmId] = useState("");
  const [openEng, setOpenEng] = useState<string | null>(null);

  const engagements = useQuery({ queryKey: ["impl-engagements"], queryFn: () => authFetch("/tenant/implementation-partners") as Promise<Engagement[]>, retry: false });
  const firms = useQuery({ queryKey: ["impl-firms"], queryFn: () => authFetch("/tenant/implementation-partners/firms") as Promise<Firm[]>, retry: false });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["impl-engagements"] });

  const engage = useMutation({
    mutationFn: () => authFetch("/tenant/implementation-partners", { method: "POST", body: JSON.stringify({ consultancyFirmId: firmId }) }),
    onSuccess: () => { setFirmId(""); invalidate(); },
  });
  const endEngagement = useMutation({
    mutationFn: (id: string) => authFetch(`/tenant/implementation-partners/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const authError = engagements.error as Error | null;
  const is401 = authError?.message?.includes("unauthenticated") || authError?.message?.includes("401");

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("impl.title")} description={t("impl.sub")} />

      {is401 ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">{t("impl.signin")}</CardContent></Card>
      ) : (
        <>
          <Can perm="tenant.invite_user">
            <Card>
              <CardHeader><CardTitle className="text-base">{t("impl.engage")}</CardTitle><CardDescription>{t("impl.engage.sub")}</CardDescription></CardHeader>
              <CardContent>
                {firms.data && firms.data.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("impl.firms.empty")}</p>
                ) : (
                  <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); if (firmId) engage.mutate(); }}>
                    <div className="grid gap-1.5">
                      <Label htmlFor="firm">{t("impl.firm")}</Label>
                      <Select value={firmId} onValueChange={setFirmId}>
                        <SelectTrigger id="firm" className="w-72"><SelectValue placeholder={t("impl.firm.pick")} /></SelectTrigger>
                        <SelectContent>
                          {(firms.data ?? []).map((f) => <SelectItem key={f.id} value={f.id}>{f.tradeName || f.legalName}{f.certified ? " ✓" : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" disabled={!firmId || engage.isPending}><Handshake className="size-4" />{engage.isPending ? t("impl.engaging") : t("impl.engage.btn")}</Button>
                    {engage.isError && <span className="text-sm text-destructive">{(engage.error as Error).message}</span>}
                  </form>
                )}
              </CardContent>
            </Card>
          </Can>

          <Card>
            <CardHeader><CardTitle className="text-base">{t("impl.engagements")}</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-3">
              {engagements.isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
              {engagements.data && engagements.data.length === 0 && <p className="text-sm text-muted-foreground">{t("impl.none")}</p>}
              {(engagements.data ?? []).map((e) => (
                <div key={e.id} className="rounded-lg border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{e.firmName}</span>
                      <Badge variant={e.status === "active" ? "default" : "secondary"}>{e.status}</Badge>
                      <span className="text-xs text-muted-foreground">{e.consultants} {t("impl.consultants")}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setOpenEng(openEng === e.id ? null : e.id)}>{t("impl.manage")}</Button>
                      {e.status === "active" && (
                        <Can perm="tenant.invite_user">
                          <Button size="sm" variant="ghost" onClick={() => endEngagement.mutate(e.id)} disabled={endEngagement.isPending}><Power className="size-4" />{t("impl.end")}</Button>
                        </Can>
                      )}
                    </div>
                  </div>
                  {openEng === e.id && <ConsultantPanel engagement={e} />}
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function ConsultantPanel({ engagement }: { engagement: Engagement }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("consultant");
  const key = ["impl-consultants", engagement.id];
  const consultants = useQuery({ queryKey: key, queryFn: () => authFetch(`/tenant/implementation-partners/${engagement.id}/consultants`) as Promise<Consultant[]>, retry: false });
  const invalidate = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["impl-engagements"] }); };
  const invite = useMutation({
    mutationFn: () => authFetch(`/tenant/implementation-partners/${engagement.id}/consultants`, { method: "POST", body: JSON.stringify({ email: email.trim(), role }) }),
    onSuccess: () => { setEmail(""); invalidate(); },
  });
  const revoke = useMutation({
    mutationFn: (ipuId: string) => authFetch(`/tenant/implementation-partners/${engagement.id}/consultants/${ipuId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  return (
    <div className="mt-3 flex flex-col gap-3 border-t pt-3">
      <ul className="flex flex-col gap-1">
        {(consultants.data ?? []).filter((c) => !c.revoked).map((c) => (
          <li key={c.id} className="flex items-center justify-between text-sm">
            <span>{c.fullName || c.email} <Badge variant="outline" className="ml-1">{c.role}</Badge> <Badge variant="secondary" className="ml-1">{c.status}</Badge></span>
            <Can perm="tenant.invite_user">
              <Button size="sm" variant="ghost" onClick={() => revoke.mutate(c.id)} disabled={revoke.isPending}><Trash2 className="size-4" /></Button>
            </Can>
          </li>
        ))}
        {consultants.data && consultants.data.filter((c) => !c.revoked).length === 0 && <li className="text-sm text-muted-foreground">{t("impl.noConsultants")}</li>}
      </ul>
      {engagement.status === "active" && (
        <Can perm="tenant.invite_user">
          <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (email.trim()) invite.mutate(); }}>
            <div className="grid gap-1.5"><Label htmlFor={`em-${engagement.id}`}>{t("impl.inviteEmail")}</Label><Input id={`em-${engagement.id}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-64" /></div>
            <div className="grid gap-1.5"><Label htmlFor={`rl-${engagement.id}`}>{t("impl.role")}</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id={`rl-${engagement.id}`} className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="consultant">{t("impl.role.consultant")}</SelectItem><SelectItem value="firm_admin">{t("impl.role.firmAdmin")}</SelectItem></SelectContent>
              </Select>
            </div>
            <Button type="submit" size="sm" disabled={!email.trim() || invite.isPending}><UserPlus className="size-4" />{t("impl.invite")}</Button>
            {invite.isError && <span className="text-sm text-destructive">{(invite.error as Error).message}</span>}
          </form>
        </Can>
      )}
    </div>
  );
}
